import PropTypes from 'prop-types';
import React, { createRef, useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { v4 as uuid } from 'uuid';
import { Icon } from 'design-comuni-plone-theme/components/ItaliaTheme';
import {
  RichTextSection,
  SideMenu,
  RichText,
  richTextHasContent,
} from 'design-comuni-plone-theme/components/ItaliaTheme/View';
import {
  Card,
  CardBody,
  Col,
  Row,
  Button,
  NotificationManager,
} from 'design-react-kit';
import { flattenToAppURL } from '@plone/volto/helpers';
import { UniversalLink } from '@plone/volto/components';
import { viewDate } from 'design-comuni-plone-theme/helpers';
import { renderPDCItemValue } from 'design-comuni-plone-theme/helpers';
import {
  PrenotazioneDeleteModal,
  PrenotazioneCardIndirizzoOrario,
} from '@redturtle/volto-io-prenoto/components/View/PrenotazioneView';
import moment from 'moment';

const messages = defineMessages({
  booking_confirmed: {
    id: 'booking_confirmed',
    defaultMessage: 'Appuntamento confermato',
  },
  booking_pending: {
    id: 'booking_pending',
    defaultMessage: 'Appuntamento in attesa di conferma',
  },
  booking_refused: {
    id: 'booking_refused',
    defaultMessage: 'Appuntamento annullato',
  },
  booking_label: {
    id: 'booking_label',
    defaultMessage: 'La prenotazione è in stato:',
  },
  bookingConfirmedMessage: {
    id: 'booking_confirmedMessage',
    defaultMessage: "L'appuntamento è fissato per ",
  },
  bookingPendingMessage: {
    id: 'booking_pendingMessage',
    defaultMessage: "L'appuntamento è stato richiesto per ",
  },
  bookingRefusedMessage: {
    id: 'booking_refusedMessage',
    defaultMessage: "L'appuntamento era fissato per ",
  },
  bookingMessage: {
    id: 'bookingMessage',
    defaultMessage: 'Orario selezionato: ',
  },
  bookingSendMail: {
    id: 'booking_sendMail',
    defaultMessage: 'Abbiamo inviato la conferma all’email:',
  },
  bookingSendMailPending: {
    id: 'booking_sendMailPending',
    defaultMessage: 'Eventuali comunicazioni saranno inviate a:',
  },
  bookingSendType: {
    id: 'booking_sendType',
    defaultMessage: 'Tipologia di prenotazione:',
  },
  bookingSendCode: {
    id: 'booking_sendCode',
    defaultMessage: 'Codice prenotazione:',
  },
  bookingSendGate: {
    id: 'booking_gate',
    defaultMessage: 'Postazione:',
  },
  bookingStaffNotes: {
    id: 'booking_staffNotes',
    defaultMessage: 'Note:',
  },
  cosa_serve: {
    id: 'booking_cosa_serve',
    defaultMessage: 'Cosa serve',
  },
  date_hours_from: {
    id: 'booking_date_hours_from',
    defaultMessage: 'dalle ore',
  },
  date_hours_to: {
    id: 'booking_date_hours_to',
    defaultMessage: 'alle ore',
  },
  at_time: { id: 'booking_at_time', defaultMessage: 'alle ore' },
  address_title: {
    id: 'booking_address_title',
    defaultMessage: 'Indirizzo',
  },
  preview_download: {
    id: 'booking_preview_download',
    defaultMessage: 'Stampa il riepilogo',
  },
  booking_cancel: {
    id: 'booking_cancel',
    defaultMessage: 'Cancella prenotazione',
  },
  booking_history: {
    id: 'booking_history',
    defaultMessage: 'Storico della prenotazione',
  },
});

const PrenotazioneConferma = ({
  result,
  bookingExist = false,
  scrollToTop,
}) => {
  const intl = useIntl();
  moment.locale(intl.locale);
  const blockID = uuid();

  // Variable to open delete modal
  const [isOpen, toggleModal] = useState(false);

  /* SIDEMENU */
  const documentBody = createRef();
  const [sideMenuElements, setSideMenuElements] = useState(null);

  const [loadDelete, setLoadDelete] = useState(false);

  const reloadPage = () => {
    setTimeout(() => {
      window.location.href = '/prenotazione-appuntamenti-uffici';
    }, 4000);
  };

  useEffect(() => {
    if (documentBody.current) {
      if (__CLIENT__) {
        setSideMenuElements(documentBody.current);
      }
    }
  }, [documentBody]);
  return (
    <>
      <div className="PageHeaderWrapper mb-4">
        <Row className="mb-2 mb-lg-0 page-header">
          <Col lg="8" className="pb-2 mx-4 page-header-left">
            <h1 className="d-flex align-items-center">
              {result.booking_status === 'refused' ? (
                <>
                  <Icon
                    className="me-2"
                    color="danger"
                    icon="it-close-circle"
                  />
                  {intl.formatMessage(messages.booking_refused)}
                </>
              ) : result.booking_status === 'pending' ? (
                <>
                  <Icon className="me-2" color="primary" icon={'it-clock'} />
                  {intl.formatMessage(messages.booking_pending)}
                </>
              ) : result.booking_status === 'confirmed' ? (
                <>
                  <Icon
                    className="me-2"
                    color="primary"
                    icon={'it-check-circle'}
                  />
                  {intl.formatMessage(messages.booking_confirmed)}
                </>
              ) : (
                <>
                  <Icon
                    className="me-2"
                    color="danger"
                    icon="it-close-circle"
                  />
                  {intl.formatMessage(messages.booking_label)}{' '}
                  {result.booking_status_label}
                </>
              )}
            </h1>
            <p>
              {result.booking_status === 'refused'
                ? intl.formatMessage(messages.bookingRefusedMessage)
                : result.booking_status === 'pending'
                  ? intl.formatMessage(messages.bookingPendingMessage)
                  : result.booking_status === 'confirmed'
                    ? intl.formatMessage(messages.bookingConfirmedMessage)
                    : intl.formatMessage(messages.bookingMessage)}{' '}
              <span className="text-capitalize">
                <strong>
                  {moment(result.booking_date).format('dddd')}{' '}
                  {moment(result.booking_date).format('DD MMMM YYYY')}
                </strong>
              </span>{' '}
              {intl.formatMessage(messages.at_time)}{' '}
              <strong>{moment(result.booking_date).format('HH:mm')}</strong>{' '}
              {/* {intl.formatMessage(messages.date_hours_from)}{' '}
              <strong>{moment(result.booking_date).format('HH:mm')}</strong>{' '}
              {intl.formatMessage(messages.date_hours_to)}{' '}
              <strong>
                {moment(result.booking_expiration_date).format('HH:mm')}
              </strong> */}
            </p>
            <ul className="list-unstyled">
              {/* CODICE PRENOTAZIONE */}
              {result.booking_code && (
                <li>
                  {intl.formatMessage(messages.bookingSendCode)}{' '}
                  <strong>{result.booking_code}</strong>
                </li>
              )}
              {/* TIPOLOGIA PRENOTAZIONE */}
              <li>
                {intl.formatMessage(messages.bookingSendType)}{' '}
                <strong>{result.booking_type}</strong>
              </li>

              {/* POSTAZIONE */}
              {result?.booking_gate && (
                <li>
                  {intl.formatMessage(messages.bookingSendGate)}{' '}
                  <strong>{result.booking_gate}</strong>
                </li>
              )}

              {/* NOTE DELLA STANZA */}
              {result?.staff_notes && (
                <li>
                  {intl.formatMessage(messages.bookingStaffNotes)}{' '}
                  <strong>{result.staff_notes}</strong>
                </li>
              )}
            </ul>
            {/* MAIL DI INVIO */}
            {result?.email &&
              result?.email !== '' &&
              result?.notify_on_confirm &&
              (result?.booking_status === 'confirmed' ||
                result?.booking_status === 'pending') && (
                <p>
                  {result?.booking_status === 'confirmed'
                    ? intl.formatMessage(messages.bookingSendMail)
                    : result?.booking_status === 'pending' &&
                      intl.formatMessage(messages.bookingSendMailPending)}
                  <br />
                  <strong>{result.email}</strong>
                </p>
              )}
            {/* BOTTONE SCARICA RIEPILOGO */}
            <Button
              outline
              color="primary"
              className="mt-2 d-print-none"
              onClick={() => {
                window.print();
              }}
            >
              <Icon
                color="primary"
                className="me-2"
                size="sm"
                icon="it-download"
              />
              {intl.formatMessage(messages.preview_download)}
            </Button>

            {/* BOTTONE CANCELLA PRENOTAZIONE */}
            {bookingExist && (
              <Button
                color="danger"
                className="mt-2 ms-4 d-print-none"
                disabled={loadDelete}
                onClick={() => {
                  toggleModal(!isOpen);
                }}
              >
                <Icon
                  color="white"
                  className="me-2"
                  size="sm"
                  icon="it-delete"
                />
                {intl.formatMessage(messages.booking_cancel)}
              </Button>
            )}
          </Col>
        </Row>

        {/* MESSAGGI */}
        {result?.messages?.length > 0 && (
          <Row className="border-top mt-4">
            <Col className="pb-2 mx-4">
              <h3 className="mt-3">
                {intl.formatMessage(messages.booking_history)}
              </h3>
              <div className="messages-wrapper">
                {result.messages.map((message) => (
                  <Card className="shadow-sm mb-4 message no-after">
                    <CardBody>
                      <div className="card-date mb-2">
                        {viewDate(intl.locale, message.date, 'DD/MM/YYYY')}
                      </div>

                      <p
                        dangerouslySetInnerHTML={{
                          __html: message.message,
                        }}
                        className="card-description mb-1"
                      />
                    </CardBody>
                  </Card>
                ))}
              </div>
            </Col>
          </Row>
        )}
      </div>
      <div className="container">
        <Row className="border-top mx-0 mt-4 pb-5">
          {/* MENU LATERALE */}
          <aside className="mt-2 col-lg-3">
            <SideMenu data={sideMenuElements} content_uid={blockID} />
          </aside>
          <section
            className="col col-lg-8 offset-lg-1 mt-5 it-page-sections-container"
            id="main-content-section"
            ref={documentBody}
          >
            {/* COSA SERVE */}
            {richTextHasContent(result.cosa_serve) && (
              <RichTextSection
                tag_id="needed"
                title={intl.formatMessage(messages.cosa_serve)}
                hasBg
                show_title={false}
                p="3"
                lighthouseId="service-needed"
              >
                <h2 className="mb-3">
                  {intl.formatMessage(messages.cosa_serve)}
                </h2>
                <RichText data={result.cosa_serve} />
              </RichTextSection>
            )}

            {/* INDIRIZZO UFFICIO */}
            {result?.booking_office && (
              <RichTextSection
                tag_id="indirizzo"
                show_title={false}
                title={intl.formatMessage(messages.address_title)}
              >
                <h2 className="my-3">
                  {intl.formatMessage(messages.address_title)}
                </h2>
                <Card className="card-teaser border-left-card rounded shadow p-3 card-small w-auto flex-column">
                  <h5 className="card-title fw-bold">
                    <UniversalLink
                      href={flattenToAppURL(result.booking_office['@id'])}
                      data-element="service-generic-access"
                    >
                      {result.booking_office.title}
                    </UniversalLink>
                  </h5>
                  <CardBody className="p-0 pt-3 w-100">
                    <PrenotazioneCardIndirizzoOrario
                      address={
                        result.booking_office?.sede?.[0] ||
                        result.booking_address
                      }
                      contacts={result.booking_office?.contact_info}
                    />
                  </CardBody>
                </Card>
              </RichTextSection>
            )}
          </section>
        </Row>
      </div>
      {/* {result?.booking_folder && (
        <div className="full-width h-auto mb-n3">
          <FeedbackForm pathname={result.booking_folder['@id']} />
        </div>
      )} */}

      {/* MODALE CANCELLA PRENOTAZIONE */}
      <PrenotazioneDeleteModal
        isOpen={isOpen}
        toggleModal={toggleModal}
        booking={result}
        setLoadDelete={setLoadDelete}
        functionAfterDelete={reloadPage}
      />

      {/* TOAST DI NOTIFICA CANCELLAZIONE */}
      <NotificationManager />
    </>
  );
};

PrenotazioneConferma.propTypes = {
  result: PropTypes.object,
};

export default PrenotazioneConferma;
